import constants from '@/constants'
import moment from 'moment'
import extenso from 'extenso'

function toStr (value) {
  return value ? value.toString() : ''
}

function onlyNumbers (input) {
  return toStr(input).replace(/\D+/g, '') || '0'
}

// Uncaught RangeError: toFixed() digits argument must be between 0 and 20 at Number.toFixed
function between (min, n, max) {
  return Math.max(min, Math.min(n, max))
}

function fixed (precision) {
  return between(0, precision, 20)
}

const numbersToCurrency = (numbers, precision) => {
  const exp = 10 ** precision
  const float = parseFloat(numbers) / exp
  return float.toFixed(fixed(precision))
}

function addThousandSeparator (integer, separator) {
  return integer.replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`)
}

function joinIntegerAndDecimal (integer, decimal, separator) {
  return decimal ? integer + separator + decimal : integer
}

const format = (
  input,
  opt = { prefix: '', suffix: '', thousands: '.', decimal: ',', precision: 2 }
) => {
  if (!input) return '0'

  if (typeof input === 'number') {
    input = input.toFixed(fixed(opt.precision)) // eslint-disable-line
  }
  const negative = input.indexOf('-') >= 0 ? '-' : ''

  const numbers = onlyNumbers(input)
  const currency = numbersToCurrency(numbers, opt.precision)
  const parts = toStr(currency).split('.')
  let integer = parts[0]
  const decimal = parts[1]
  integer = addThousandSeparator(integer, opt.thousands)
  return (
    opt.prefix +
    negative +
    joinIntegerAndDecimal(integer, decimal, opt.decimal) +
    opt.suffix
  )
}

const unformat = (input, precision) => {
  const negative = input.indexOf('-') >= 0 ? -1 : 1
  const numbers = onlyNumbers(input)
  const currency = numbersToCurrency(numbers, precision)
  return parseFloat(currency) * negative
}

const formatDate = (value, format = constants.FORMAT_DATE) => {
  return moment(value).format(format)
}

const parseDate = (value, format = constants.FORMAT_DATE) => {
  return moment(value, format)
}

const formatCEP = value => value !== null ? value.replace(/(\d{5})(\d{3})/g, '$1-$2') : ''

const formatEmptyRow = (row, column, prop) => prop || '-'

const formatEmptyRowNA = (row, column, prop) => prop || 'N/A'

const isJson = (str) => {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

const getFileNameWithoutExtension = fileName => fileName.substring(0, fileName.lastIndexOf('.')) || fileName
const converteNumeroExtenso = (numero, tipoConversao) => {
  let numeroInteiro = 0
  let centimetros = 0
  let string = ''

  if (numero === 0) {
    return ''
  }

  numeroInteiro = numero.toString().split('.')[0]
  centimetros = numero.toString().split('.')[1]

  if (centimetros !== undefined) {
    if (tipoConversao === 'metros') {
      if (centimetros.toString().length > 2 && parseInt(centimetros, 10) > 0) {
        if (extenso(parseInt(centimetros.substring(0, 2), 10)) !== undefined) {
          string += extenso(parseInt(centimetros.substring(0, 2), 10)) + ' centímetros e '
        }
        if (centimetros.toString().length > 3 && parseInt(centimetros, 10) > 0) {
          string += extenso(parseInt(centimetros.substring(2, 4), 10)) + ' milímetros'
        } else if (parseInt(centimetros, 10) > 0) {
          string += extenso(parseInt(centimetros.substring(2, 4), 10) * 10) + ' milímetros'
        }
      } else if (parseInt(centimetros, 10) > 0) {
        if (centimetros.toString().length === 1) {
          string += extenso(parseInt(centimetros, 10) * 10) + ' centímetros'
        } else {
          string += extenso(parseInt(centimetros, 10)) + ' centímetros'
        }
      }
      if (parseInt(numeroInteiro, 10) > 0) {
        string = (extenso(parseInt(numeroInteiro, 10)) + (numeroInteiro > 1 ? ' metros ' : ' metro ')) + ' e ' + string
      }
    } else if (tipoConversao === 'hectares') {
      if (centimetros.toString().length > 2 && parseInt(centimetros, 10) > 0) {
        if (extenso(parseInt(centimetros.substring(0, 2), 10)) !== undefined) {
          string += extenso(parseInt(centimetros.substring(0, 2), 10)) + ' ares e '
        }
        if (centimetros.toString().length > 3 && parseInt(centimetros, 10) > 0) {
          string += extenso(parseInt(centimetros.substring(2, 4), 10)) + ' centiares'
        } else if (parseInt(centimetros, 10) > 0) {
          string += extenso(parseInt(centimetros.substring(2, 4), 10) * 10) + ' centiares'
        }
      } else if (parseInt(centimetros, 10) > 0) {
        if (centimetros.toString().length === 1) {
          string += extenso(parseInt(centimetros, 10) * 10) + ' ares'
        } else {
          string += extenso(parseInt(centimetros, 10)) + ' ares'
        }
      }
      if (parseInt(numeroInteiro, 10) > 0) {
        string = (extenso(typeof numeroInteiro === 'string' ? parseInt(numeroInteiro, 10) : numeroInteiro) + (numeroInteiro > 1 ? ' hectares ' : ' hectare ')) + ' e ' + string
      }
    }
  } else if (centimetros === undefined) {
    if (tipoConversao === 'metros') {
      string = (extenso(typeof numeroInteiro === 'string' ? parseInt(numeroInteiro, 10) : numeroInteiro) + (numeroInteiro > 1 ? ' metros ' : ' metro '))
    } else if (tipoConversao === 'hectares') {
      string = (extenso(typeof numeroInteiro === 'string' ? parseInt(numeroInteiro, 10) : numeroInteiro) + (numeroInteiro > 1 ? ' hectares ' : ' hectare '))
    }
  }
  return string.toUpperCase()
}

const decimalPorExtenso = (numero, tipo) => {
  const partesUnidades = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove']
  const partesDezenas = ['', 'dez', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa']
  const partesEspeciais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove']

  let resultado = 'zero vírgula'

  let inteiro = Math.floor(numero * 100)
  let dezenas = Math.floor(inteiro / 10)
  let unidades = inteiro % 10

  if (dezenas === 1) {
    resultado += ' ' + partesEspeciais[unidades]
  } else {
    if (dezenas > 0) {
      resultado += ' ' + partesDezenas[dezenas]
    }
    if (unidades > 0) {
      resultado += ' ' + partesUnidades[unidades]
    }
  }

  return `${resultado.trim()} ${tipo}`.toUpperCase()
}

const normalize = (text) => {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

const padraoMoedaBrasil = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2
})

const compararData = (a, b) => {
  // Divide a data em MM e YYYY
  const dateASplited = [a.mes, a.ano] // a.data.split('/')
  const dateBSplited = [b.mes, b.ano] // b.data.split('/')

  let dateAFormated = ''
  let dateBFormated = ''

  // Para saber se a data está vindo no formato MM/yyyy ou MM/dd/yyyy
  if (dateASplited.length === 2) {
    // Formata para o padrão americano, paa poder dar o new Date correto MM/dd/yyyy
    dateAFormated = `${dateASplited[0]}/01/${dateASplited[1]}`
    dateBFormated = `${dateBSplited[0]}/01/${dateBSplited[1]}`
  } else {
    dateAFormated = `${dateASplited[1]}/${dateASplited[0]}/${dateASplited[2]}`
    dateBFormated = `${dateBSplited[1]}/${dateBSplited[0]}/${dateBSplited[2]}`
  }

  // Cria a nova data para comparar corretamente
  const newDateA = new Date(dateAFormated)
  const newDateB = new Date(dateBFormated)

  if (newDateA < newDateB) {
    return -1
  }
  if (newDateA > newDateB) {
    return 1
  }

  return 0
}

const checkIsVencido = (data, dataVencimento) => {
  const dataFormatada = data.split('/')
  const newData = new Date(`${dataFormatada[1]}/${dataFormatada[0]}/${dataFormatada[2]}`)
  let newDataVencimento = new Date()

  if (typeof dataVencimento === 'string') {
    newDataVencimento = new Date(dataVencimento.split('-'))
  } else {
    newDataVencimento = new Date(`${dataVencimento.monthValue}/${dataVencimento.dayOfMonth}/${dataVencimento.year}`)
  }

  return newData > newDataVencimento
}

const checkIsAvencer = (data, dataVencimento) => {
  const dataFormatada = data.split('/')
  const DataHoje = new Date(`${dataFormatada[1]}/${dataFormatada[0]}/${dataFormatada[2]}`)
  let newDataVencimento = new Date()

  if (typeof dataVencimento === 'string') {
    newDataVencimento = new Date(dataVencimento.split('-'))
  } else {
    newDataVencimento = new Date(`${dataVencimento.monthValue}/${dataVencimento.dayOfMonth}/${dataVencimento.year}`)
  }

  const vencimento = newDataVencimento.getTime()

  const DataAmanha = new Date()
  DataAmanha.setDate(DataAmanha.getDate() + 1)

  return vencimento >= DataHoje.getTime() && vencimento <= DataAmanha.getTime()
}

const formatMunicipio = (row, column, prop) => {
  if (prop) {
    return prop?.length === 0 ? '-' : prop[0].nome
  }

  return '-'
}

const formatDateStringToJs = (value) => {
  let partesData = value.split('/')
  return new Date(`${partesData[1]}/${partesData[0]}/${partesData[2]}`)
}

const getExtensionFile = (filename) => {
  if (filename) {
    // tratamento para caso haja . no meio do nome do arquivo
    let indexExtension = filename.lastIndexOf('.')
    return filename.substring(indexExtension + 1).toLowerCase() || ''
  }
  return '-'
}

const municipiosIlha = (idMunicipio) => {
  return [2111300, 2109452, 2107506, 2111201].includes(idMunicipio)
}

export const stringToArrayBuffer = (string) => {
  let binaryString = window.atob(string)
  let len = binaryString.length
  let bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

const zerosDepoisDaVirgula = (quantidade) => {
  if (!quantidade || typeof quantidade !== 'number') {
    return '00'
  }
  return Array(quantidade).fill('0').join('')
}

export const formataValor = (value, decimal, comVirgula) => {
  if (!value || value === '') {
    return ''
  }
  if (comVirgula) {
    const r = /\./g
    const newValue = value.replace(r, '').replace(',', '.')
    const numeroFormatado = parseFloat(newValue).toLocaleString('pt-BR', {
      maximumFractionDigits: decimal
    })
    if (!numeroFormatado.includes(',') && numeroFormatado !== '0') {
      return `${numeroFormatado},${zerosDepoisDaVirgula(decimal)}`
    }
    return numeroFormatado
  } else {
    const numeroFormatado = parseFloat(value).toLocaleString('pt-BR', {
      maximumFractionDigits: decimal
    })
    if (!numeroFormatado.includes(',') && numeroFormatado !== '0') {
      return `${numeroFormatado},${zerosDepoisDaVirgula(decimal)}`
    }
    return numeroFormatado
  }
}

export const numberToStringFourDigits = (number) => {
  if (number && typeof number === 'number') {
    return number.toFixed(4).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.')
  }
  return number
}

export const numberToStringDouble = (number) => {
  if (number) {
    return number.toFixed(4).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.')
  }
  return null
}

export const conversaoArea = (area, tipoArea) => {
  if (area && tipoArea === 'LINHA') {
    return numberToStringDouble(area * 0.33)
  }
  if (area && tipoArea === 'M2') {
    return numberToStringDouble(area / 10000)
  }
  return numberToStringDouble(area * 3.03)
}

export const conversaoAreaTexto = (area, tipoArea) => {
  const valor = conversaoArea(area, tipoArea)
  if (area && tipoArea === 'LINHA') {
    return `${valor} hectare(s)`
  }
  if (area && tipoArea === 'M2') {
    return `${valor} hectare(s)`
  }
  return `${valor} linha(s)`
}

export const ajustarDataFinal = (data) => {
  if (data && data[1]) {
    const dataFinalAjustada = new Date(data[1])
    dataFinalAjustada.setHours(23, 59, 59, 0)
    return [data[0], dataFinalAjustada]
  } else {
    return []
  }
}

export const formataStringYearMonthToBr = (data) => {
  // data vindo no formato: yyyy-MM
  const dataSplit = data.split('-')
  return `${dataSplit[1]}/${dataSplit[0]}`
}

export const validaTipoCadastro = (tipoCadastro) => {
  return ['EMPRESA', 'AGRICULTOR', 'PARCEIROS_FORNECEDORES', 'TECNICO'].includes(tipoCadastro)
}

export default {
  format,
  unformat,
  formatDate,
  formatEmptyRow,
  isJson,
  formatCEP,
  getFileNameWithoutExtension,
  formatEmptyRowNA,
  normalize,
  converteNumeroExtenso,
  compararData,
  padraoMoedaBrasil,
  checkIsVencido,
  checkIsAvencer,
  formatMunicipio,
  parseDate,
  formatDateStringToJs,
  getExtensionFile,
  formataValor,
  municipiosIlha,
  decimalPorExtenso,
  numberToStringFourDigits,
  ajustarDataFinal
}



// WEBPACK FOOTER //
// ./src/utils/utils.js